<template>
  <div :class="$style.config">
    <div v-if="computedMediaImage" :class="$style.mediaImage">
      <AspectRatio
        :ratio="
          computedMediaImage?.fields?.file?.details?.image?.width +
          'x' +
          computedMediaImage?.fields?.file?.details?.image?.height
        "
        inline-block
      >
        <BasePicture
          :img-class="$style.img"
          :src="`${computedMediaImage?.fields?.file?.url}?q=70&fm=webp`"
          :alt="computedMediaImage?.fields?.title || ''"
        />
      </AspectRatio>
    </div>
    <WithBackdrop :class="$style.wrap" :backdrop="data?.fields?.backdrop">
      <div :class="$style.container">
        <header v-if="data?.fields?.header || data?.fields?.subheader">
          <BaseHeading v-if="data?.fields?.header" size="h2">
            {{ data?.fields?.header }}
          </BaseHeading>
          <BaseHeading v-if="data?.fields?.subheader" size="h3">
            {{ data?.fields?.subheader }}
          </BaseHeading>
        </header>
        <template v-if="computedSquareMessage">
          <MarkedParser :md="computedSquareMessage" />
        </template>
        <template v-else>
          <MarkedParser :class="[$style.copy, 'u-type-p1']" :md="copyBody" />
          <template v-if="loggedIn">
            <div v-if="stores?.length" :class="$style.ctaLayout">
              <BaseButton
                v-if="connectCta?.fields?.link && connectCta?.fields?.label"
                :href="connectCta?.fields?.link"
              >
                {{ connectCta?.fields?.label }}
              </BaseButton>
              <BaseButton
                v-if="optOutCta?.fields?.link && optOutCta?.fields?.label"
                :href="optOutCta?.fields?.link"
              >
                {{ optOutCta?.fields?.label }}
              </BaseButton>
            </div>
          </template>
          <template v-else>
            <BaseButton
              v-if="loginCta?.fields?.link && loginCta?.fields?.label"
              @click="handleLogin"
            >
              {{ loginCta?.fields?.label }}
            </BaseButton>
          </template>
        </template>
      </div>
    </WithBackdrop>
  </div>
</template>

<script setup lang="ts">
import fm from 'front-matter'

import type { TypeContentBlockWithoutUnresolvableLinksResponse } from '~/types/contentful'

interface IFrontMatterCopy {
  successMessage?: string | undefined
  deniedMessage?: string | undefined
}

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

const { handleLogin } = useAuthLogin()
const userStore = useUserStore()
const { loggedIn, stores } = storeToRefs(userStore)

const computedMediaImage = computed(() => {
  const media = props.data?.fields?.media
  const isImage = media?.fields?.file?.contentType?.includes('image/')
  return isImage ? media : null
})

const computedSquareMessage = computed(() => {
  const route = useRoute()
  const copyFrontMatter = fm<IFrontMatterCopy>(props.data?.fields?.copy || '')
  switch (route.query.status) {
    case 'accepted':
      return copyFrontMatter?.attributes?.successMessage || ''
    case 'denied':
      return copyFrontMatter?.attributes?.deniedMessage || ''
    default:
      return ''
  }
})

function frontMatterCopyBody() {
  return fm<IFrontMatterCopy>(props.data?.fields?.copy || '')?.body || ''
}

const loginCta = findCtaByIdentifier(props.data?.fields?.ctas, 'square-login')
const connectCta = findCtaByIdentifier(
  props.data?.fields?.ctas,
  'square-connect',
)
const optOutCta = findCtaByIdentifier(
  props.data?.fields?.ctas,
  'square-opt-out',
)

const copyBody = frontMatterCopyBody()
</script>

<style lang="scss" module>
.config {
  --wrap-padding--base: 40px 20px;
  --wrap-padding--md: 60px 60px;
  --wrap-padding: var(--wrap-padding--base);

  --container-max-width--base: 520px;
  --container-max-width--md: 900px;
  --container-max-width: var(--container-max-width--base);

  --container-gap--base: 16px;
  --container-gap--md: 24px;
  --container-gap: var(--container-gap--base);

  --container-grid-template-columns--base: 1fr;
  --container-grid-template-columns--md: 1fr;
  --container-grid-template-columns: var(
    --container-grid-template-columns--base
  );

  --mediaImage-max-width: 520px;

  @include media('md') {
    --wrap-padding: var(--wrap-padding--md);
    --container-max-width: var(--container-max-width--md);
    --container-gap: var(--container-gap--md);
    --container-grid-template-columns: var(
      --container-grid-template-columns--md
    );
  }
}

.wrap {
  color: var(--square-connect-color, #000);
  display: grid;
  place-items: center;
  min-height: var(--square-connect-min-height, 420px);
  padding: var(--wrap-padding);
}

.container {
  display: grid;
  grid-template-columns: var(--container-grid-template-columns);
  gap: var(--container-gap);
  align-items: center;

  max-width: var(--container-max-width);
  margin: 0 auto;
}

.content {
  display: grid;
  place-items: center;
  gap: 18px;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: var(--roboto-condensed);
  }

  h1,
  h2 {
    @include fluid-type(26px, 42px);
  }
}

.copy {
  @include fluid-type(16px, 19px);
}

.mediaImage {
  width: 100%;
}

.img {
  max-width: 100%;
  object-fit: contain;
}

.ctaLayout {
  display: grid;
  gap: 16px;

  @include media('sm') {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
